import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Image,
	Infobox,
	Layout,
	Spinner,
	styled,
	Text,
	TextAlignment,
	TextInput,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import * as React from 'react';
import { hasFormFieldError, updateValidateFormField, validateNoEmpty } from '../../../helpers/form-helper';
import { useSetState } from '../../../hooks/get-state-hook';
import { DataVehicles, Form, FormDef } from '@esportal/portal-service';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import { HelpContainer } from './help-container';
import { useTrackingManager } from '../../../tracking/use-tracking-manager';
import { ErrorType, Vehicle } from '@esportal/portal-service';
import { formatOwnership } from '../../../tracking/tracking-helper';
import { LcvLayer } from '../layer/lcvLayer';
import { PortalContext } from '../../../context/portal-context';
import { AddVehiclePending } from './add-vehicle-pending';

const InputsContainer = styled.div`
	& div > div > div {
		flex: 1;
		border: 1px solid transparent;
	}

	& span {
		color: #000e26;
	}

	& label {
		color: #000e26;
	}
`;

const validateRegistrationPlate = (value: string) => {
	// Matrículas modernas: 0000XXX
	const modernRegex = /^[0-9]{4}[BCDFGHJKLMNPRSTVWXYZ]{3}$/;

	// Matrículas antiguas: letra provincia + 4 números + 1–2 letras finales
	const oldRegex = /^[A-Z]{1,2}[0-9]{4}[A-Z]{1,2}$/;

	if (modernRegex.test(value) || oldRegex.test(value)) {
		return null;
	}

	return 'Formato de matrícula inválido';
};

const validateVin = (value: string) => {
	const regex = /^[A-Z0-9]{17}$/;
	return regex.test(value) ? null : 'Formato de número de bastidor inválido';
};

const isLcvVin = (value: string) => {
	return value.startsWith('WV1') || value.startsWith('WV2') || value.startsWith('WV4');
};

export const ConfirmVehicleData: React.FC<{
	vehicles: Vehicle[] | undefined;
	vin: string | null;
	plate: string | null;
	fichaTecnica: File;
	permisoCirculacion: File;
	registerToClub?: boolean;
	onAutoClose?: () => void;
	onError: (errorType: ErrorType) => void;
}> = ({
	vehicles,
	vin,
	plate,
	fichaTecnica,
	permisoCirculacion,
	registerToClub,
	onAutoClose: handleVehicleLayerAutoClose,
	onError,
}) => {
	const { portalService } = React.useContext(PortalContext);
	const [canSendForm, setCanSendForm] = React.useState<boolean>(false);
	const [createdCompleted, setCreatedCompleted] = React.useState<boolean>(false);
	const [isCreating, setIsCreating] = React.useState<string | null>(null);
	const [showError, setShowError] = React.useState(false);
	const [activeLcvPopup, setActiveLcvPopup] = React.useState(false);
	const lcvLayerRef = React.useRef<LayerHandleV2<any>>(null);

	const handleConfirmClick = () => {
		const vinValue = form.fields.vin.value;
		if (isLcvVin(vinValue)) {
			setActiveLcvPopup(true);
			return;
		}
		handleSubmit();
	};

	const trackingManager = useTrackingManager();

	const handleVolverOnClick = () => {
		trackingManager.trackClubVWButtonClick(
			'Volver',
			{ contentId: 'Close Person Vehicles layer' },
			{ vehicle: formatOwnership(vehicles) },
		);

		handleVehicleLayerAutoClose?.();

		//Al cerrar la layer ponemos un timeout de 1 segundo ya que si cerramos esta layer antes de ejecutar el timeout la siguiente layer no se abrira.
		//La solucion es lanzar este evento antes de cerrar la layer, asi nos aseguramos que se ejecute aunque la layer se cierre antes de tiempo.

		// setTimeout(() => {
		// 	handleVehicleLayerAutoClose?.();
		// }, 1000);
	};

	const [form, setForm, getForm] = useSetState<Form>(
		new Form({
			registrationPlate: new FormDef(plate || '', [validateNoEmpty, validateRegistrationPlate]),
			vin: new FormDef(vin || '', [validateNoEmpty, validateVin]),
		}),
	);

	React.useEffect(() => {
		if (plate) {
			handleFieldChange({ target: { name: 'registrationPlate', value: plate } } as any, true);
		}

		if (vin) {
			handleFieldChange({ target: { name: 'vin', value: vin } } as any, true);
		}
	}, [plate, vin]);

	const handleFieldChange = (e: React.SyntheticEvent<HTMLInputElement>, modeBlur: boolean) => {
		const { name, value } = e.target as HTMLInputElement;

		getForm().then((form: Form) => {
			const formResult: Form = updateValidateFormField(form, name, value, modeBlur);
			setForm(formResult);
			setCanSendForm(formResult.isValid);
		});
	};

	const handleSubmit = () => {
		setIsCreating(form.fields.vin.value);

		trackingManager.trackClubVWButtonClick(
			'Confirmar',
			{ contentId: 'Confirm vin and registrationPlate' },
			{ vehicle: formatOwnership(vehicles) },
		);

		getForm().then((form: Form) => {
			portalService
				.createPersonVehicle(
					form.fields.vin.value,
					form.fields.registrationPlate.value,
					fichaTecnica,
					permisoCirculacion,
				)
				.subscribe({
					next: (updatedVehicles: DataVehicles | null) => {
						if (updatedVehicles && form.fields.vin.value) {
							setCreatedCompleted(true);
						}
						setIsCreating(null);
					},
					error: (error) => {
						console.log(error.message);
						if (error.message?.includes('400')) {
							setShowError(true);

							trackingManager.trackClubVWErrorMessageLoad(
								{
									code: '400',
									message: 'Uy, ha habido un error con los datos de tu petición',
								},
								{
									contentId: `Error: ${ErrorType.AddVehicle} - ${ErrorType.ConfirmData}`,
								},
							);

							setIsCreating(null);
						} else {
							setIsCreating(null);
							onError(ErrorType.ConfirmData);
						}
					},
				});
		});
	};

	return (
		<>
			{!createdCompleted ? (
				<Container
					horizontalAlign={'center'}
					verticalAlign={'center'}
					wrap={'always'}
					padding={{
						top: ContainerPadding.dynamic0200,
						bottom: ContainerPadding.dynamic0200,
					}}
					stretchContent
				>
					{registerToClub && (
						<Layout
							data-testid="confirm-add-car-club-logo__layout"
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 7 },
									{ name: 'a', columns: 10 },
									{ name: '.', columns: 7 },
								],
								[Breakpoints.b560]: [
									{ name: '.', columns: 8 },
									{ name: 'a', columns: 8 },
									{ name: '.', columns: 8 },
								],
								[Breakpoints.b960]: [
									{ name: '.', columns: 10 },
									{ name: 'a', columns: 4 },
									{ name: '.', columns: 10 },
								],
								[Breakpoints.b1920]: [
									{ name: '.', columns: 11 },
									{ name: 'a', columns: 2 },
									{ name: '.', columns: 11 },
								],
							}}
							allowOverflowingContent
						>
							<Container
								padding={{ bottom: ContainerPadding.dynamic0250 }}
								wrap={'always'}
								horizontalAlign={'center'}
								stretchContent
							>
								<Image
									src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-club/logo_club.svg"
									alt="Logo Club VW"
								/>
							</Container>
						</Layout>
					)}
					<Layout
						data-testid="confirm-add-car__layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 3 },
								{ name: 'd', columns: 18 },
								{ name: '.', columns: 3 },
								{ name: '.', columns: 7 },
								{ name: 'e', columns: 10 },
								{ name: '.', columns: 7 },
								{ name: '.', columns: 1 },
								{ name: 'f', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b560]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 4 },
								{ name: 'd', columns: 16 },
								{ name: '.', columns: 4 },
								{ name: '.', columns: 7 },
								{ name: 'e', columns: 10 },
								{ name: '.', columns: 7 },
								{ name: '.', columns: 1 },
								{ name: 'f', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'd', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 8 },
								{ name: 'e', columns: 8 },
								{ name: '.', columns: 8 },
								{ name: '.', columns: 1 },
								{ name: 'f', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'd', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 9 },
								{ name: 'e', columns: 6 },
								{ name: '.', columns: 9 },
								{ name: '.', columns: 1 },
								{ name: 'f', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1600]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'd', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 9 },
								{ name: 'e', columns: 6 },
								{ name: '.', columns: 9 },
								{ name: '.', columns: 1 },
								{ name: 'f', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'd', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 10 },
								{ name: 'e', columns: 4 },
								{ name: '.', columns: 10 },
								{ name: '.', columns: 1 },
								{ name: 'f', columns: 22 },
								{ name: '.', columns: 1 },
							],
						}}
						allowOverflowingContent
					>
						<Container
							horizontalAlign={'center'}
							padding={{
								bottom: ContainerPadding.dynamic0250,
							}}
							wrap={'always'}
						>
							<Text
								appearance={TokenTextAppearance.headline300}
								textAlign={TextAlignment.center}
								color={TokenTextColor.primary}
							>
								¿Nos confirmas los datos?
							</Text>
						</Container>

						<InputsContainer>
							<Container
								wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
								gutter={{ [Breakpoints.b960]: ContainerGutter.dynamic0130 }}
								padding={{
									bottom: ContainerPadding.dynamic0150,
								}}
								horizontalAlign={'space-between'}
							>
								<Container horizontalAlign={'center'} stretchContent>
									<TextInput
										appearance={hasFormFieldError(form.fields.registrationPlate) ? 'error' : 'default'}
										label="Matrícula*"
										name="registrationPlate"
										value={form.fields.registrationPlate.value}
										onChange={(e) => handleFieldChange(e, false)}
										message="Este campo solo admite letras y números, sin espacios ni guiones."
									/>
								</Container>
								<Container horizontalAlign={'center'} stretchContent>
									<TextInput
										appearance={hasFormFieldError(form.fields.vin) ? 'error' : 'default'}
										label="Número de bastidor*"
										name="vin"
										value={form.fields.vin.value}
										onChange={(e) => handleFieldChange(e, false)}
										message="Este campo solo admite letras y números, sin espacios ni guiones."
									/>
								</Container>
							</Container>
						</InputsContainer>

						<Container>
							<Text>
								{showError && (
									<Infobox aria-label="Notification" aria-live="polite">
										<Container
											gutter={ContainerGutter.dynamic0050}
											wrap="always"
											horizontalAlign="center"
											stretchContent
										>
											<Container gutter={ContainerGutter.dynamic0020} verticalAlign="center">
												<Text textAlign={TextAlignment.left} bold>
													Uy, ha habido un error con los datos de tu petición
												</Text>
												<Text textAlign={TextAlignment.left}>
													Contacta con nosotros y te ayudaremos. ¡No olvides adjuntar tus documentos para agilizar el
													proceso!
												</Text>
											</Container>

											<Container gutter={ContainerGutter.dynamic0040} verticalAlign="center">
												<CTA tag="a" emphasis="tertiary">
													atencioncliente@volkswagen.es
												</CTA>
											</Container>
										</Container>
									</Infobox>
								)}
							</Text>
						</Container>

						<Container
							horizontalAlign={'center'}
							padding={{
								bottom: ContainerPadding.dynamic0150,
								top: showError ? ContainerPadding.dynamic0150 : ContainerPadding.static0,
							}}
						>
							<Text
								appearance={TokenTextAppearance.copy200}
								textAlign={TextAlignment.center}
								color={TokenTextColor.primary}
							>
								Enviando este formulario declaras estar informado de la{' '}
								<CTA
									tag="a"
									href={'https://www.volkswagen.es/es/volkswagen-espana/politica-privacidad.html'}
									aria-label="aria label"
									emphasis="tertiary"
									target="_blank"
									onClick={() => {
										trackingManager.trackClubVWLinkClick(
											'Política de privacidad',
											{
												contentId: 'Navigate to privacy policy',
											},
											{ vehicle: formatOwnership(vehicles) },
										);
									}}
								>
									<Text appearance={TokenTextAppearance.copy200} color={TokenTextColor.primary}>
										política de privacidad
									</Text>
								</CTA>
							</Text>
						</Container>

						<Container
							horizontalAlign={'center'}
							verticalAlign={'center'}
							padding={{
								bottom: ContainerPadding.dynamic0250,
							}}
							stretchContent
						>
							{isCreating ? (
								<CTA tag="button" emphasis="primary" disabled={true} stretchContent>
									<Spinner />
								</CTA>
							) : (
								<CTA
									tag="button"
									emphasis="primary"
									disabled={!canSendForm}
									onClick={handleConfirmClick}
									stretchContent
								>
									<Text>Confirmar</Text>
								</CTA>
							)}
						</Container>

						<Container wrap={'always'} gutter={ContainerGutter.static250}>
							<Container
								horizontalAlign={'center'}
								padding={{
									bottom: ContainerPadding.dynamic0200,
								}}
								stretchContent
							>
								<HelpContainer />
							</Container>

							<Text
								appearance={TokenTextAppearance.copy200}
								color={TokenTextColor.secondary}
								textAlign={TextAlignment.left}
								bold
							>
								Texto legal de Volkswagen
							</Text>
							<Text
								appearance={TokenTextAppearance.copy100}
								color={TokenTextColor.secondary}
								textAlign={TextAlignment.left}
							>
								Te informamos de que tus datos personales serán tratados por Volkswagen Group España Distribución
								S.A.U., en calidad de responsable del tratamiento, con la finalidad de atender tus consultas y
								solicitudes, realizar un control de calidad sobre los productos y servicios solicitados, realizar
								encuestas de opinión y estudios de mercado con fines estadísticos; y, en caso de que nos hayas dado tu
								consentimiento expreso, para las finalidades descritas en los consentimientos adicionales. Informarte
								que puedes ajercer los derechos que te confiere la normativa de protección de datos mediante solicitud
								escrita a{' '}
								<CTA tag="a" emphasis="tertiary" href="mailto:atencionalcliente@volkswagen.es">
									<Text appearance={TokenTextAppearance.copy100} color={TokenTextColor.secondary}>
										{' '}
										atencionalcliente@volkswagen.es
									</Text>
								</CTA>
								.
							</Text>
							<Text
								appearance={TokenTextAppearance.copy100}
								color={TokenTextColor.secondary}
								textAlign={TextAlignment.left}
							>
								Puedes consultar la información adicional sobre el tratamiento de tus datos en nuestra{' '}
								<CTA
									tag="a"
									emphasis="tertiary"
									href="https://www.volkswagen.es/es/volkswagen-espana/politica-privacidad.html"
									target="_blank"
									onClick={() => {
										trackingManager.trackClubVWLinkClick(
											'Política de privacidad',
											{
												contentId: 'Navigate to privacy policy',
											},
											{ vehicle: formatOwnership(vehicles) },
										);
									}}
								>
									<Text appearance={TokenTextAppearance.copy100} color={TokenTextColor.secondary}>
										política de privacidad
									</Text>
								</CTA>
								.
							</Text>
						</Container>
					</Layout>
				</Container>
			) : registerToClub && createdCompleted ? (
				<AddVehiclePending plate={plate} />
			) : (
				<Container
					horizontalAlign={'center'}
					wrap={'always'}
					padding={{
						top: ContainerPadding.dynamic0200,
						bottom: ContainerPadding.dynamic0200,
					}}
					stretchContent
				>
					{(() => {
						trackingManager.trackClubVWInfoMessageLoad({ contentId: 'Adding vehicle' });
						return null;
					})()}
					<Layout
						data-testid="success-add-car-layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 7 },
								{ name: 'c', columns: 10 },
								{ name: '.', columns: 7 },
							],
							[Breakpoints.b560]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 8 },
								{ name: 'c', columns: 8 },
								{ name: '.', columns: 8 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 9 },
								{ name: 'c', columns: 6 },
								{ name: '.', columns: 9 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 3 },
								{ name: 'b', columns: 18 },
								{ name: '.', columns: 3 },
								{ name: '.', columns: 9 },
								{ name: 'c', columns: 6 },
								{ name: '.', columns: 9 },
							],
							[Breakpoints.b1600]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 3 },
								{ name: 'b', columns: 18 },
								{ name: '.', columns: 3 },
								{ name: '.', columns: 9 },
								{ name: 'c', columns: 6 },
								{ name: '.', columns: 9 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 20 },
								{ name: '.', columns: 2 },
								{ name: '.', columns: 3 },
								{ name: 'b', columns: 18 },
								{ name: '.', columns: 3 },
								{ name: '.', columns: 10 },
								{ name: 'c', columns: 4 },
								{ name: '.', columns: 10 },
							],
						}}
						allowOverflowingContent
					>
						<Container horizontalAlign={'center'}>
							<Text
								appearance={TokenTextAppearance.headline300}
								textAlign={TextAlignment.center}
								color={TokenTextColor.primary}
							>
								¡Listo! Estamos añadiendo este Volkswagen a tu perfil.
							</Text>
						</Container>

						<Container
							wrap={'always'}
							horizontalAlign={'center'}
							padding={{
								top: ContainerPadding.dynamic0130,
							}}
						>
							<Text
								appearance={TokenTextAppearance.bigcopy100}
								textAlign={TextAlignment.center}
								color={TokenTextColor.primary}
							>
								Y, ¿ahora qué?
							</Text>
							<Text
								appearance={TokenTextAppearance.bigcopy100}
								textAlign={TextAlignment.center}
								color={TokenTextColor.primary}
							>
								Este proceso puede tardar entre 24 y 48 horas. Vuelve a iniciar sesión pasado ese tiempo para comprobar
								el estado de tu petición.
							</Text>
						</Container>

						<Container
							horizontalAlign={'center'}
							padding={{
								top: ContainerPadding.dynamic0200,
								bottom: ContainerPadding.dynamic0200,
							}}
							stretchContent
						>
							<CTA tag="button" emphasis="primary" size="large" onClick={handleVolverOnClick} stretchContent>
								Volver
							</CTA>
						</Container>
					</Layout>
				</Container>
			)}
			<LcvLayer isVisible={activeLcvPopup} setIsVisible={setActiveLcvPopup} layerRef={lcvLayerRef} />
		</>
	);
};
